<template>
  <div
    class="chat-context-selector"
    :class="{ 'chat-context-selector--standalone': standalone }"
    data-testid="chatbot__context-selector"
  >
    <div
      v-if="isContextLoading"
      class="chat-context-selector__loader"
      data-testid="chatbot__context-selector-loader"
    >
      <div
        v-for="section in 2"
        :key="`context-skeleton-${section}`"
        class="chat-context-selector__skeleton-section"
      >
        <v-skeleton-loader
          type="text"
          class="chat-context-selector__skeleton-label"
        />
        <v-skeleton-loader
          type="image"
          class="chat-context-selector__skeleton-input"
        />
      </div>
    </div>

    <!-- Cycle Selection (75% width) -->
    <div
      v-show="!isContextLoading"
      class="chat-context-selector__cycle chat-context-selector__section"
    >
      <div class="chat-context-selector__label">
        <img src="/img/icons/Cycle.svg" alt="Cycle" class="chat-context-selector__icon" />
        <span>Select Cycle</span>
      </div>
      <v-tooltip bottom content-class="tooltip-bottom">
        <template v-slot:activator="{ on, attrs }">
          <div
            class="chat-context-selector__cycle-select"
            v-bind="attrs"
            v-on="on"
            data-testid="chatbot__cycle-dropdown-tooltip-activator"
          >
            <lb-dropdown
              :items="cycleItems"
              itemtext="description"
              itemvalue="_id"
              :value="selectedCycle"
              @input="onCycleChange"
              label=""
              placeholder="Choose a cycle"
              :disabled="isContextLoading"
              data-testid="chatbot__cycle-dropdown"
            />
          </div>
        </template>
        <span data-testid="chatbot__cycle-dropdown-tooltip-text">{{ selectedCycleTooltip }}</span>
      </v-tooltip>
    </div>

    <!-- Role Selection (25% width) -->
    <div
      v-show="!isContextLoading"
      class="chat-context-selector__role chat-context-selector__section"
    >
      <div class="chat-context-selector__label">
        <img src="/img/icons/RolePermissionManage.svg" alt="Role" class="chat-context-selector__icon" />
        <span>Select Role</span>
      </div>
      <lb-dropdown
        :items="roleItems"
        itemtext="name"
        itemvalue="id"
        :value="selectedRole"
        @input="onRoleChange"
        label=""
        placeholder="Choose a role"
        :disabled="isContextLoading"
        data-testid="chatbot__role-dropdown"
        class="chat-context-selector__role-select"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { ROLE_LIST } from '../utils/constants';

export default {
  name: 'ChatContextSelector',

  props: {
    standalone: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      roleItems: ROLE_LIST,
      isCycleLoading: false,
    };
  },

  computed: {
    ...mapState({
      storeProject: state => state.chatbot.project,
      storeRole: state => state.chatbot.role,
      userId: state => state.user?.id,
      tasklistUserType: state => state.tasklistUserType,
    }),

    projectList() {
      return this.$store.getters['project/projectList'] || [];
    },

    cycleItems() {
      return this.projectList;
    },

    selectedCycle() {
      return this.storeProject;
    },

    selectedRole() {
      return this.storeRole;
    },

    selectedCycleTooltip() {
      const selectedProject = this.projectList.find(project => project?._id === this.selectedCycle);
      return selectedProject?.description || 'Choose a cycle';
    },

    isContextLoading() {
      return this.isCycleLoading && this.cycleItems.length === 0;
    },
  },

  watch: {
    cycleItems: {
      immediate: true,
      handler(items) {
        this.syncSelectionWithCycles(items);
      },
    },
    tasklistUserType: {
      immediate: true,
      handler() {
        this.syncRoleFromTasklistTab();
      },
    },
  },

  async mounted() {
    await this.ensureCycleItemsLoaded();
    this.syncRoleFromTasklistTab();
    this.initDefaults();
  },

  methods: {
    syncRoleFromTasklistTab() {
      const preferredRole = (this.tasklistUserType || '').toLowerCase();
      const isPreferredRoleValid = this.roleItems.some(role => role.id === preferredRole);

      if (!isPreferredRoleValid || this.storeRole === preferredRole) return;

      this.$store.dispatch('chatbot/setRole', preferredRole);
    },

    async ensureCycleItemsLoaded() {
      if (this.cycleItems.length > 0) return;

      const candidateActions = ['project/fetchProjectListV3', 'project/fetchProjectList'];

      try {
        this.isCycleLoading = true;
        
        for (const action of candidateActions) {
          try {
            await this.$store.dispatch(action);
            return; // Success, exit early
          } catch (error) {
            // Try next action
            continue;
          }
        }
        
        // If we get here, all actions failed
        console.error('[ChatContextSelector]: ensureCycleItemsLoaded - no valid action found');
      } catch (error) {
        console.error('[ChatContextSelector]: ensureCycleItemsLoaded error', error);
      } finally {
        this.isCycleLoading = false;
      }
    },

    syncSelectionWithCycles(items = []) {
      if (!Array.isArray(items) || items.length === 0) return;

      const hasCurrentSelection = items.some(item => item?._id === this.storeProject);

      if (!hasCurrentSelection) {
        const firstId = items[0]._id;
        if (firstId) {
          this.$store.dispatch('chatbot/setProject', firstId);
          this.applyDefaultRole(firstId);
        }
        return;
      }

      if (!this.storeRole) {
        this.applyDefaultRole(this.storeProject);
      }
    },

    initDefaults() {
      if (!this.storeProject && this.cycleItems.length > 0) {
        const firstId = this.cycleItems[0]._id;
        this.$store.dispatch('chatbot/setProject', firstId);
        this.applyDefaultRole(firstId);
      } else if (this.storeProject && !this.storeRole) {
        this.applyDefaultRole(this.storeProject);
      }
    },

    applyDefaultRole(projectId) {
      if (!projectId) return;

      const preferredRole = (this.tasklistUserType || '').toLowerCase();
      const isPreferredRoleValid = this.roleItems.some(role => role.id === preferredRole);

      if (isPreferredRoleValid) {
        this.$store.dispatch('chatbot/setRole', preferredRole);
        return;
      }

      const project = this.projectList.find(p => p._id === projectId);

      if (!project) {
        this.$store.dispatch('chatbot/setRole', 'preparer');
        return;
      }

      const inManagerList = Array.isArray(project?.managerList) && project.managerList.includes(this.userId);
      this.$store.dispatch('chatbot/setRole', inManagerList ? 'manager' : 'preparer');
    },

    onCycleChange(val) {
      this.$store.dispatch('chatbot/setProject', val);
      this.applyDefaultRole(val);
    },

    onRoleChange(val) {
      this.$store.dispatch('chatbot/setRole', val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/ChatContextSelector.scss';
</style>
